import apiClient from "../core/apiClient";
import { AgentCodeGroup, GroupByReportedHandlerProduct, GroupByReportedPeriod, GroupByContactId, GroupByContactProductHandler, MimshakComparisonDTO, MonthlyReturn, ReportedPeriod, DepositsWithdrawalsTransfers } from "../interfaces/DTO/agent/mimshakGroupBy";
import { MimshakData } from "../interfaces/DTO/mimshakData";

export interface SortedByContactParams {
  contact_ids: Array<number>;
  handler_code?: string;
  product_type?: number;
  policy_number?: string;
  skip?: number;
  limit?: number;
}

export abstract class MimshakDataServiceBase {
  protected API_URL: string;
  protected apiClient = apiClient;
  /** Query param name for the ids of /by_user_sorted — "contact_ids" on the mimshak routers, "user_ids" on user tasks */
  protected sortedIdsQueryParam = "contact_ids";

  constructor(apiUrl: string) {
    this.API_URL = apiUrl;
  }

  async getLatestRecordsByUser(user_id: number): Promise<MimshakData[]> {
    const response = await apiClient.get<MimshakData[]>(`${this.API_URL}/latest/${user_id}`);
    return response.data;
  }

  async getByIdentifiers(contact_id: number, product_type: number, policy_number: string, reported_period?: string): Promise<MimshakData | null> {
    const params: Record<string, string | number> = {
      user_id: contact_id,
      product_type,
      policy_number
    };

    if (reported_period) {
      params.reported_period = reported_period;
    }

    const response = await this.apiClient.get<MimshakData | null>(
      `${this.API_URL}/by_identifiers`,
      { params }
    );
    return response.data;
  }

  async getMimshaksDataByUserAndPeriod(
    contact_id: number,
    period: string,
  ): Promise<MimshakData[]> {
    const response = await apiClient.get<MimshakData[]>(`${this.API_URL}/by_period/${contact_id}/${period}`);
    return response.data;
  }

  async getMimshaksDataByMultipleUsersAndPeriod(
    contactIds: number[],
    period: string
  ): Promise<MimshakData[]> {
    const params = new URLSearchParams();
    contactIds.forEach(id => params.append("contact_ids", id.toString()));
    params.append("period", period);
    const response = await apiClient.get<MimshakData[]>(
      `${this.API_URL}/by_period_batch?${params.toString()}`
    );
    return response.data;
  }

  async getLatestByMultipleContacts(contactIds: number[]): Promise<MimshakData[]> {
    // Use the new /latest_batch endpoint that returns only the latest record per contact+handler+product+policy
    const params = new URLSearchParams();
    contactIds.forEach(id => params.append("contact_ids", id.toString()));
    const response = await apiClient.get<MimshakData[]>(
      `${this.API_URL}/latest_batch?${params.toString()}`
    );
    return response.data;
  }

  async getByContactSorted(params: SortedByContactParams): Promise<MimshakData[]> {
    const response = await apiClient.get<MimshakData[]>(`${this.API_URL}/by_user_sorted`, {
      params,
      paramsSerializer: (rawParams) => {
        // axios passes Record<string, any> — cast, so a renamed field can't silently become undefined here
        const params = rawParams as SortedByContactParams;
        const query = new URLSearchParams();
        params.contact_ids.forEach((id: number) => query.append(this.sortedIdsQueryParam, id.toString()));
        if (params.handler_code) query.append("handler_code", params.handler_code);
        if (params.product_type !== undefined) query.append("product_type", params.product_type.toString());
        if (params.policy_number) query.append("policy_number", params.policy_number);
        if (params.skip !== undefined) query.append("skip", params.skip.toString());
        if (params.limit !== undefined) query.append("limit", params.limit.toString());
        return query.toString();
      },
    });
    return response.data;
  }

  // פונקציה לביצוע GROUP BY לפי user_id
  async groupByContactId(): Promise<GroupByContactId[]> {
    const response = await apiClient.get<GroupByContactId[]>(`${this.API_URL}/groupby/user_id`);
    return response.data;
  }

  // פונקציה לביצוע GROUP BY לפי reported_period
  async groupByHandlerReportedPeriod(reportOrigin?: number, minReportedPeriod?: string): Promise<GroupByReportedPeriod[]> {
    const params: Record<string, number | string> = {};
    if (reportOrigin !== undefined) params.report_origin = reportOrigin;
    if (minReportedPeriod !== undefined) params.min_reported_period = minReportedPeriod;

    const response = await apiClient.get<GroupByReportedPeriod[]>(
      `${this.API_URL}/groupby/reported_period`,
      { params }
    );
    return response.data;
  }

  async groupByReportedHandlerProduct(reportOrigin?: number, minReportedPeriod?: string): Promise<GroupByReportedHandlerProduct[]> {
    const params: Record<string, number | string> = {};
    if (reportOrigin !== undefined) params.report_origin = reportOrigin;
    if (minReportedPeriod !== undefined) params.min_reported_period = minReportedPeriod;

    const response = await apiClient.get<GroupByReportedHandlerProduct[]>(
      `${this.API_URL}/groupby/reported_handler_product`,
      { params }
    );
    return response.data;
  }

  async groupByReportedHandlerProductNewPolicy(): Promise<GroupByReportedHandlerProduct[]> {
    const response = await apiClient.get<GroupByReportedHandlerProduct[]>(
      `${this.API_URL}/groupby/reported_handler_product_new_policy`
    );
    return response.data;
  }

  async groupByReportedHandlerProductNewForAgentPolicy(): Promise<GroupByReportedHandlerProduct[]> {
    const response = await apiClient.get<GroupByReportedHandlerProduct[]>(
      `${this.API_URL}/groupby/reported_handler_product_new_for_agent_policy`
    );
    return response.data;
  }

  async compareUserPolicies(
    userId: number,
    currentReportDate: string,
    previousReportDate?: string
  ): Promise<MimshakComparisonDTO[]> {
    const response = await apiClient.get<MimshakComparisonDTO[]>(
      `${this.API_URL}/compare-user-policies`,
      {
        params: {
          user_id: userId,
          current_report_date: currentReportDate,
          previous_report_date: previousReportDate
        }
      }
    );
    return response.data;
  }

  async compareAllPolicies(currentPeriod: string, previousPeriod: string): Promise<MimshakComparisonDTO[]> {
    const response = await apiClient.get<MimshakComparisonDTO[]>(
      `${this.API_URL}/compare_policies`,
      {
        params: {
          current_period: currentPeriod,
          previous_period: previousPeriod
        }
      }
    );
    return response.data;
  }

  async comparePoliciesForContact(contactId: number, currentPeriod: string, previousPeriod: string): Promise<MimshakComparisonDTO[]> {
    const response = await apiClient.get<MimshakComparisonDTO[]>(`${this.API_URL}/compare_policies`, {
      params: {
        current_period: currentPeriod,
        previous_period: previousPeriod,
        contact_id: contactId,
      },
    });
    return response.data;
  }

  // פונקציה לביצוע GROUP BY לפי user_id, product_type, ו-handler_code
  async groupByUserProductHandler(): Promise<GroupByContactProductHandler[]> {
    const response = await apiClient.get<GroupByContactProductHandler[]>(
      `${this.API_URL}/groupby/user_product_handler`);
    return response.data;
  }

  async groupByReportedHandlerProductForUsers(
    userIds: number[],
    reportedPeriod?: string
  ): Promise<GroupByReportedHandlerProduct[]> {
    const response = await apiClient.get<GroupByReportedHandlerProduct[]>(
      `${this.API_URL}/groupby/reported_handler_product_for_users`,
      {
        params: {
          contact_ids: userIds,
          ...(reportedPeriod ? { reported_period: reportedPeriod } : {})
        },
        paramsSerializer: (params) => {
          return Object.keys(params)
            .map((key) =>
              Array.isArray(params[key])
                ? params[key].map((val: any) => `${key}=${encodeURIComponent(val)}`).join("&")
                : `${key}=${encodeURIComponent(params[key])}`
            )
            .join("&");
        }
      }
    );
    return response.data;
  }

  async fetchGroupedCodeCounts(reportedPeriod: string): Promise<AgentCodeGroup[]> {
    const response = await apiClient.get<AgentCodeGroup[]>(
      `${this.API_URL}/agent-code-groups`,
      {
        params: {
          reported_period: reportedPeriod,
        },
      }
    );
    return response.data;
  }

  async getFundsByAdjustedProductType(
    userIds: number[],
    adjustedProductTypes: number[],
    minReportedPeriod?: string
  ): Promise<{ reported_period: string; adjusted_product_type: number; sum_funds_sum: number }[]> {
    const params = new URLSearchParams();
    userIds.forEach(id => params.append('user_ids', id.toString()));
    adjustedProductTypes.forEach(t => params.append('adjusted_product_types', t.toString()));
    if (minReportedPeriod) params.append('min_reported_period', minReportedPeriod);
    const response = await apiClient.get(`${this.API_URL}/funds/by_adjusted_product_type?${params.toString()}`);
    return response.data;
  }

  async getMonthlyReturnsForUsers(
    userIds: number[],
    productTypes: number[],
    adjustedProductTypes?: number[]
  ): Promise<MonthlyReturn[]> {
    const response = await apiClient.get<MonthlyReturn[]>(
      `${this.API_URL}/returns/monthly`,
      {
        params: {
          user_ids: userIds,
          ...(adjustedProductTypes?.length
            ? { adjusted_product_types: adjustedProductTypes }
            : { product_types: productTypes }),
        },
        paramsSerializer: (params) => {
          return Object.keys(params)
            .map((key) =>
              Array.isArray(params[key])
                ? params[key].map((val: any) => `${key}=${encodeURIComponent(val)}`).join('&')
                : `${key}=${encodeURIComponent(params[key])}`
            )
            .join('&');
        }
      }
    );
    return response.data;
  }

  // ✅ פונקציה לשליפת כל התקופות הייחודיות בחברה (לפי company_id)
  async getDistinctReportedPeriods(): Promise<ReportedPeriod[]> {
    try {
      const response = await apiClient.get<ReportedPeriod[]>(
        `${this.API_URL}/distinct_periods_for_company`
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch distinct reported periods:', error);
      throw error;
    }
  }

  // ✅ פונקציה לשליפת התקופות - contact יחיד
  async getDistinctReportedPeriodsForUser(
    userId: number
  ): Promise<ReportedPeriod[]> {
    try {
      const response = await apiClient.get<ReportedPeriod[]>(
        `${this.API_URL}/distinct_periods`,
        {
          params: { user_id: userId }, // ➡️ שליחת user_id בפרמטרים
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch distinct reported periods:', error);
      throw error;
    }
  }

  // ✅ פונקציה לשליפת התקופות - מספר contacts
  async getDistinctReportedPeriodsForContacts(
    contactIds: number[]
  ): Promise<ReportedPeriod[]> {
    try {
      const params = new URLSearchParams();
      contactIds.forEach(id => params.append("contact_ids", id.toString()));

      const response = await apiClient.get<ReportedPeriod[]>(
        `${this.API_URL}/distinct_periods?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch distinct reported periods for contacts:', error);
      throw error;
    }
  }

  /**
   * שליפת רשומות MimshakData לפי פילטרים דינמיים
   * @param user_id - מזהה המשתמש (אופציונלי)
   * @param handler_code - קוד המטפל (אופציונלי)
   * @param reported_period - תאריך הדיווח בפורמט YYYY-MM-DD (אופציונלי)
   * @param product_type - סוג המוצר (אופציונלי)
   * @param policy_number - מספר הפוליסה (אופציונלי)
   * @param skip - מספר רשומות לדלג (Pagination)
   * @param limit - מספר רשומות להחזיר (Pagination)
   * @returns Promise<MimshakData[]>
   */
  async getLightFilteredMimshakData(
    user_id?: number,
    handler_code?: string,
    reported_period?: string,
    product_type?: string,
    policy_number?: string,
    agent_code?: string,
    skip: number = 0,
    limit: number = 1000,
    report_origin?: number
  ): Promise<MimshakData[]> {
    try {
      // יצירת אובייקט עם רק הפילטרים שהועברו (לא לשים undefined ב-URL)
      const filters: Record<string, string> = {};
      if (user_id !== undefined) filters["user_id"] = user_id.toString();
      if (handler_code !== undefined) filters["handler_code"] = handler_code;
      if (reported_period !== undefined) filters["reported_period"] = reported_period;
      if (product_type !== undefined) filters["product_type"] = product_type;
      if (policy_number !== undefined) filters["policy_number"] = policy_number;
      if (agent_code !== undefined) filters["agent_code"] = agent_code;
      if (report_origin !== undefined) filters["report_origin"] = report_origin.toString();

      // הוספת Pagination
      filters["skip"] = skip.toString();
      filters["limit"] = limit.toString();

      // יצירת URL עם פרמטרים דינמיים
      const params = new URLSearchParams(filters);
      const response = await apiClient.get<MimshakData[]>(`${this.API_URL}/light/filtered?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching filtered MimshakData records:", error);
      throw error;
    }
  }

  async getLightTransferMimshakData(
    from_period?: string,
    to_period?: string,
    contact_ids?: number[],
    handler_code?: string,
    agent_code?: string,
    product_type?: string,
    policy_number?: string,
    skip: number = 0,
    limit: number = 400
  ): Promise<MimshakData[]> {
    try {
      const params = new URLSearchParams();

      if (from_period !== undefined) params.append("from_period", from_period);
      if (to_period !== undefined) params.append("to_period", to_period);
      if (contact_ids && contact_ids.length > 0) {
        contact_ids.forEach(id => params.append("contact_ids", id.toString()));
      }
      if (handler_code !== undefined) params.append("handler_code", handler_code);
      if (agent_code !== undefined) params.append("agent_code", agent_code);
      if (product_type !== undefined) params.append("product_type", product_type);
      if (policy_number !== undefined) params.append("policy_number", policy_number);

      params.append("skip", skip.toString());
      params.append("limit", limit.toString());

      const response = await apiClient.get<MimshakData[]>(`${this.API_URL}/light/transfers?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching MimshakData transfers:", error);
      throw error;
    }
  }

  async getDepositsWithdrawalsTransfersByPeriodProduct(contactIds: number[]): Promise<DepositsWithdrawalsTransfers[]> {
    const response = await apiClient.get<DepositsWithdrawalsTransfers[]>(
      `${this.API_URL}/groupby/deposits_withdrawals_by_period_product`,
      {
        params: {
          contact_ids: contactIds
        },
        paramsSerializer: (params) => {
          return Object.keys(params)
            .map((key) =>
              Array.isArray(params[key])
                ? params[key].map((val: any) => `${key}=${encodeURIComponent(val)}`).join("&")
                : `${key}=${encodeURIComponent(params[key])}`
            )
            .join("&");
        }
      }
    );
    return response.data;
  }

  /**
   * סיכום deposit_amount ו-withdrawal_amount לפי product_type עבור כל contact_id בנפרד
   * @param productType - סוג המוצר
   * @param startPeriod - תקופת התחלה (YYYY-MM-DD)
   * @param endPeriod - תקופת סיום (YYYY-MM-DD)
   * @param forecastDepositsEoy - האם לחשב תחזית הפקדות עד סוף שנה
   * @returns Promise<Array<{contact_id: number, sum_deposit_amount: number, sum_withdrawal_amount: number, forecast_eoy?: number, total_with_forecast?: number}>>
   */
  async sumDepositsByProductType(
    productType: number,
    startPeriod: string,
    endPeriod: string,
    forecastDepositsEoy: boolean = false
  ): Promise<Array<{ contact_id: number; sum_deposit_amount: number; sum_withdrawal_amount: number; forecast_eoy?: number; total_with_forecast?: number }>> {
    try {
      const params = new URLSearchParams();
      params.append("product_type", productType.toString());
      params.append("start_period", startPeriod);
      params.append("end_period", endPeriod);
      params.append("forecast_deposits_eoy", forecastDepositsEoy.toString());

      const response = await apiClient.get<Array<{ contact_id: number; sum_deposit_amount: number; sum_withdrawal_amount: number; forecast_eoy?: number; total_with_forecast?: number }>>(
        `${this.API_URL}/sum/deposits_withdrawals_by_product_type?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching year-end deposits:", error);
      throw error;
    }
  }
}
